import { PropsWithChildren, useCallback, useRef, useState } from 'react';

import {
  DataGridProProps,
  GridApiRef,
  GridRenderCellParams,
  GridRowModel,
  GridRowTreeNodeConfig,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import * as MaterialUIIcons from '../../../../app/common/controls/MaterialUIIcons';
import AdminDataGrid from '../AdminDataGrid';
import { Box, IconButton, useTheme } from '@mui/material';
import {
  TreeRowsBuilderProps,
  useTreeRowsBuilder,
} from '../../../utils/treeUtils';
import { AdminDataGridProps } from '../AdminDataGrid/AdminDataGrid';
import { TEntity } from '../../../../models/entity';
import { getFilterPredicate } from '../../../utils/filterClientUtils';
import { TypeFilter } from '../../../utils/filter';


export type TreeBaseProps<T extends TEntity> = {
  showPagination?: boolean;
  showAsTreeView?: boolean;
  haveTreeView?: boolean;
  treeClickOnRow?: boolean;
  isGroupExpandedByDefault?: (node: GridRowTreeNodeConfig) => boolean;
  setTreeView?(val: boolean): void;
  treeData?: boolean;
  getTreeDataPath?: (row: GridRowModel) => string[];
  expandedAll?: boolean;
  hideAll?: boolean;
  setTreeClickOnRow?: (val: boolean) => void;
  expandChildrenNodes?: (val: T) => void;
  expandChildrenLessNodes?: (val: T) => void;
  expandAllNodes?: () => void;
  expandLessNodes?: () => void;
  hideAllCallBack?: () => void;
}

export type TreeAdminDataGridProps<T extends TEntity> = AdminDataGridProps<T> & {
  showPagination?: boolean;
  parentFieldID: keyof T;
  propertyId: keyof T;
  parentPropertyId?: keyof T;
  refetchTreeList?: () => void;
  setIsTreeView?: (val: boolean) => void;
  getAllNode?: () => Promise<T[]>;
  getChildrenItems: (id: any, expanded: boolean) => Promise<T[]>;
  treeColumns?: DataGridProProps['columns'];
};
export interface FetchTreeArg {
  searchString: string;
  id: string;
  expandAll: boolean;
}

type GroupingCellTreeDataProps = {
  expandChildren?: (id: string | number, serverId: string | number) => Promise<void>;
  hideChildren?: (id: string | number) => void;
  filedIdName: string;
  propertyId: string;
  parentPropertyId?: string;
};

export const CustomGridTreeDataGroupingCell = (
  props: GridRenderCellParams & GroupingCellTreeDataProps
) => {

  const { rowNode, filedIdName } = props;

  const theme = useTheme();


  const apiRef = useGridApiContext();

  const handleClick = async (event: any) => {
    if (!rowNode.childrenExpanded) {
      apiRef.current?.setRowChildrenExpansion(rowNode.id, true);
      if (props.expandChildren) {
        props.expandChildren(props.row[props.parentPropertyId ?? props.propertyId], props.row[props.propertyId]);
      }
    } else {
      apiRef.current?.setRowChildrenExpansion(rowNode.id, false);
      if (props.hideChildren) props.hideChildren(props.row[props.parentPropertyId ?? props.propertyId]);
    }

    event.stopPropagation();
  };

  return (
    <Box sx={{ ml: rowNode.depth * 2 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          {props.row['hasChildren'] ? (
            <div>
              <IconButton
                edge={'start'}
                onClickCapture={handleClick}
                style={{
                  height: theme.spacing(2.5),
                  width: theme.spacing(2.5),
                  marginRight: theme.spacing(0.8),
                  paddingLeft: 0
                }}
                size="small"
              >
                {rowNode.childrenExpanded ? (
                  <MaterialUIIcons.ExpandMoreIcon onClickCapture={handleClick} fontSize="small" />
                ) : (
                  <MaterialUIIcons.ChevronRightIcon fontSize="small" />
                )}
              </IconButton>
              <span>{props.row[filedIdName]}</span>
            </div>
          ) : (
            <span style={{ marginLeft: theme.spacing(2.9) }}>{props.row[filedIdName]}</span>
          )}
        </div>
      </div>
    </Box>
  );
};

export function TreeViewAdmin<T extends { id: number | string }>(
  props: PropsWithChildren<TreeAdminDataGridProps<T>>
) {
  // const classes = useStyles();
  const {
    setIsTreeView,
    treeColumns,
    columns,
    rows: initialRow,
    propertyId,
    refetchTreeList,
    parentFieldID,
    refresh,
    getChildrenItems,
    parentPropertyId
  } = props;

  const apiRef = useRef<GridApiRef>();

  const {
    getTreeDataPath,
    expandChildren,
    hideChildren,
    handleHideAll,
    handleExpandChildrenForItem,
    handleNodeLessCallback,
    handleExpandAllNodeCallback,
    expandedEntity,
    isFetching,
    treeRows,
    handleRefresh,
    isGroupExpandedByDefault,
  } = useTreeRowsBuilder<T>({
    parentFieldID: parentFieldID,
    parentPropertyId: parentPropertyId,
    propertyId: propertyId,
    gridId: props.registerId,
    saveInnstillings: true,
    rows: initialRow,
    getAllNode: props.getAllNode,
    getChildrenItems: getChildrenItems,
  } as TreeRowsBuilderProps<T>);

  const getApiRef = useCallback((newApiRef: GridApiRef) => {
    apiRef.current = newApiRef;
  }, []);

  const [showAsTreeView, setShowAsTreeView] = useState<boolean>(true);

  const defColumns = showAsTreeView && treeColumns ? treeColumns : columns;

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    ...defColumns[0],
    renderCell: (params) => (
      <CustomGridTreeDataGroupingCell
        {...params}
        filedIdName={defColumns[0].field}
        expandChildren={expandChildren}
        hideChildren={hideChildren}
        propertyId={propertyId.toString()}
      />
    ),
  };
  const filter = useCallback(() => {
    let filteredRows = treeRows;
    if (showAsTreeView) {
      filteredRows = treeRows.filter((val: any) => {
        if (props.currentFilter?.type) // if have filter
        {
          const predicate = getFilterPredicate(props.currentFilter);
          if (val.hasChildren && expandedEntity?.treeModelInnstillingWrappers?.some(i => i.entityId === val.id)) // if element is expanded
          {
            return treeRows.some(i => i.path?.includes(val.id) && predicate(i)); // if level has children for this filter 
          }
          else {
            if (val.hasChildren) // if has element that are not expanded
              return true;
            return predicate(val); // check filter for element that doesn`t have children
          }
        }
        return true;
      });
    }
    return filteredRows.map(val => {
      if (props.currentFilter?.type) {
        const predicate = getFilterPredicate(props.currentFilter);
        if (val.hasChildren
          && expandedEntity?.treeModelInnstillingWrappers?.some(i => i.entityId === val.id)
          && !treeRows.some(i => i.path.includes(val.id) && (i.path.slice(i.path.indexOf(val.id) + 1).length >= 1)  // check sub-levels on exisist
            && predicate(i))) // if has children and doesn`t have children with current filter set hasChildren as false
        {
          return { ...val, hasChildren: false };
        }
      }
      return val;
    });
  }, [props.currentFilter, showAsTreeView, treeRows, expandedEntity]);

  let rows = showAsTreeView ? treeRows : props.rows;

  if (props.filterType === TypeFilter.client && showAsTreeView)
    rows = filter();

  return (
    <AdminDataGrid<T>
      {...props}
      showPagination={props.showPagination}
      haveTreeView={true}
      columns={defColumns}
      showAsTreeView={showAsTreeView}
      setTreeView={(val: boolean) => {
        setShowAsTreeView(val);
        if (setIsTreeView !== undefined) setIsTreeView(val);
      }}
      paginationMode='server'
      rows={rows}
      treeData={true}
      getApiGridRef={getApiRef}
      groupingColDef={groupingColDef}
      hideAllCallBack={handleHideAll}
      expandChildrenNodes={handleExpandChildrenForItem}
      expandChildrenLessNodes={handleNodeLessCallback}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getTreeDataPath={getTreeDataPath}
      loading={props.loading || isFetching}
      treeClickOnRow={false}
      expandAllNodes={handleExpandAllNodeCallback}
      refresh={showAsTreeView ? refetchTreeList ? refetchTreeList : handleRefresh : refresh}
      setTreeClickOnRow={() => { }}
    />
  );
}
